import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { AlertCircle, FileText, Info, Loader2, ChevronDown, ChevronUp, ClipboardList, Shield, GitBranch } from 'lucide-react';
import coverageApiService from '../../../services/coverage-api.service';
import CoverageNotesDisplay from './CoverageNotesDisplay';
import StructuredNotesDisplay from './StructuredNotesDisplay';
import MedicalNotesTab from './MedicalNotesTab';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';

/**
 * NotesTab - Main Notes tab component with Formulary and Medical Coverage Notes
 */
const NotesTab = ({
  noteId,
  productId,
  formularyId,
  notesAvailable,
  indications = [],
  indicationsLoading = false,
  coverageStatus = 'no_data',
  paRequired = false,
  stRequired = false,
  cachedCoverageNotes,
  cachedPANotes,
  cachedSTNotes,
  cachedMedicalNotes,
  onNotesUpdate,
}) => {
  const [activeTab, setActiveTab] = useState('formulary');
  const [expandedSections, setExpandedSections] = useState({
    coverage: true,  // Default to expanded
    pa: false,
    st: false
  });
  
  // Get user and search context for analytics
  const userAttributes = useSelector((state) => state.auth?.userAttributes || {});
  const searchParams = useSelector((state) => state.coverage?.searchParams || {});
  
  const toggleSection = async (section) => {
    const wasExpanded = expandedSections[section];
    const willBeExpanded = !wasExpanded;
    
    setExpandedSections(prev => ({
      ...prev,
      [section]: willBeExpanded
    }));
    
    // Track notes section expansion
    if (willBeExpanded) {
      try {
        const noteTypeMap = {
          'coverage': 'Coverage Notes',
          'pa': 'Prior Authorization',
          'st': 'Step Therapy'
        };
        
        await sendToKinesis({
          eventtype: 'COVERAGE_NOTES_VIEWED',
          userid: userAttributes.sub || userAttributes['cognito:username'] || 'anonymous',
          useremail: userAttributes.email || 'unknown',
          planId: searchParams.selectedPlan?.planid || '',
          planName: searchParams.selectedPlan?.planname || '',
          payer: searchParams.selectedPlan?.payer || '',
          pbm: searchParams.selectedPlan?.pbm || '',
          drugId: searchParams.selectedDrug?.drugid || '',
          drugName: searchParams.selectedDrug?.drugname || '',
          noteType: noteTypeMap[section] || section,
          hasContent: section === 'coverage' ? !!cachedCoverageNotes : 
                      section === 'pa' ? !!cachedPANotes : !!cachedSTNotes,
          timestamp: new Date().toISOString()
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }
    }
  };
  
  // Loading states only (data comes from cache)
  const [coverageNotesLoading, setCoverageNotesLoading] = useState(false);
  const [paNotesLoading, setPANotesLoading] = useState(false);
  const [stNotesLoading, setSTNotesLoading] = useState(false);

  // Fetch coverage notes when tab is selected
  const fetchCoverageNotes = useCallback(async () => {
    if (!noteId || cachedCoverageNotes || coverageStatus === 'not_covered') {
      if (cachedCoverageNotes) {
        console.log('[Cache] Cache hit - using cached coverage notes');
      }
      return;
    }
    
    console.log('[Cache] Cache miss - fetching coverage notes');
    setCoverageNotesLoading(true);
    
    try {
      const response = await coverageApiService.getCoverageNotes(noteId);
      
      if (response && response.result) {
        onNotesUpdate('coverageNotes', response);
      } else {
        onNotesUpdate('coverageNotes', { error: 'Failed to load coverage notes' });
      }
    } catch (error) {
      console.error('Error fetching coverage notes:', error);
      onNotesUpdate('coverageNotes', { error: 'An error occurred while loading coverage notes' });
    } finally {
      setCoverageNotesLoading(false);
    }
  }, [noteId, cachedCoverageNotes, coverageStatus, onNotesUpdate]);

  // Fetch PA structured notes
  const fetchPANotes = useCallback(async () => {
    if (cachedPANotes || coverageStatus === 'not_covered' || !paRequired) {
      if (cachedPANotes) {
        console.log('[Cache] Cache hit - using cached PA notes');
      }
      return;
    }
    
    console.log('[Cache] Cache miss - fetching PA notes');
    setPANotesLoading(true);
    
    try {
      const response = await coverageApiService.getPAStructuredNotes(productId, formularyId);
      
      if (response && response.result) {
        onNotesUpdate('paNotes', response);
      } else {
        onNotesUpdate('paNotes', { error: 'Failed to load prior authorization notes' });
      }
    } catch (error) {
      console.error('Error fetching PA notes:', error);
      onNotesUpdate('paNotes', { error: 'An error occurred while loading prior authorization notes' });
    } finally {
      setPANotesLoading(false);
    }
  }, [cachedPANotes, coverageStatus, paRequired, productId, formularyId, onNotesUpdate]);

  // Fetch ST structured notes
  const fetchSTNotes = useCallback(async () => {
    if (cachedSTNotes || coverageStatus === 'not_covered' || !stRequired) {
      if (cachedSTNotes) {
        console.log('[Cache] Cache hit - using cached ST notes');
      }
      return;
    }
    
    console.log('[Cache] Cache miss - fetching ST notes');
    setSTNotesLoading(true);
    
    try {
      const response = await coverageApiService.getSTStructuredNotes(productId, formularyId);
      
      if (response && response.result) {
        onNotesUpdate('stNotes', response);
      } else {
        onNotesUpdate('stNotes', { error: 'Failed to load step therapy notes' });
      }
    } catch (error) {
      console.error('Error fetching ST notes:', error);
      onNotesUpdate('stNotes', { error: 'An error occurred while loading step therapy notes' });
    } finally {
      setSTNotesLoading(false);
    }
  }, [cachedSTNotes, coverageStatus, stRequired, productId, formularyId, onNotesUpdate]);

  // Load data when sections are expanded
  useEffect(() => {
    if (activeTab === 'formulary') {
      if (expandedSections.coverage) {
        fetchCoverageNotes();
      }
      if (expandedSections.pa) {
        fetchPANotes();
      }
      if (expandedSections.st) {
        fetchSTNotes();
      }
    }
  }, [activeTab, expandedSections, fetchCoverageNotes, fetchPANotes, fetchSTNotes]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">Coverage Notes & Requirements</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Detailed coverage notes, prior authorization, and step therapy requirements
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-muted flex-wrap h-auto gap-2 p-2 rounded-lg border shadow-sm">
            <TabsTrigger value="formulary" className="text-xs sm:text-sm md:text-base font-bold min-h-[44px] px-3 sm:px-4 md:px-6 transition-all duration-200 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Pharmacy Benefit Notes
            </TabsTrigger>
            <TabsTrigger value="medical" className="text-xs sm:text-sm md:text-base font-bold min-h-[44px] px-3 sm:px-4 md:px-6 transition-all duration-200 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Medical Benefit Notes
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="formulary" className="mt-6">
            {coverageStatus === 'not_covered' ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <Info className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base font-semibold text-foreground">
                    Formulary Notes Not Provided
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    The plan has not published formulary-specific notes for this drug. Check Medical Coverage Notes for additional requirements.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Plan Coverage Notes Section */}
                <Collapsible
                  open={expandedSections.coverage}
                  onOpenChange={() => toggleSection('coverage')}
                >
                  <Card className="border-2 shadow-sm">
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <ClipboardList className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold">Plan Coverage Notes</h3>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Detailed coverage notes and requirements from the plan
                              </p>
                            </div>
                          </div>
                          {expandedSections.coverage ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground transition-transform duration-200" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-4">
                  {!notesAvailable || !noteId ? (
                    <Alert className="text-sm">
                      <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <AlertDescription className="text-xs sm:text-sm">
                        Coverage notes not provided by this plan for this drug combination
                      </AlertDescription>
                    </Alert>
                  ) : coverageNotesLoading ? (
                    <div className="p-4 sm:p-6 md:p-8">
                      <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-3 sm:px-4 bg-muted/30 rounded-lg border-2 border-dashed">
                        <Loader2 className="h-8 w-8 sm:h-10 sm:w-10 text-primary animate-spin mb-3 sm:mb-4" />
                        <p className="text-xs sm:text-sm font-medium text-foreground mb-1">
                          Loading Plan Coverage Notes...
                        </p>
                        <p className="text-[10px] sm:text-xs font-normal text-muted-foreground text-center max-w-md">
                          Fetching detailed coverage notes for this plan
                        </p>
                      </div>
                    </div>
                  ) : cachedCoverageNotes?.error ? (
                    <Alert variant="destructive" className="text-sm">
                      <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <AlertDescription className="text-xs sm:text-sm">{cachedCoverageNotes.error}</AlertDescription>
                    </Alert>
                  ) : cachedCoverageNotes ? (
                    <CoverageNotesDisplay notes={cachedCoverageNotes.result.RuleNotes} />
                  ) : null}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>

                {/* Prior Authorization Section */}
                <Collapsible
                  open={expandedSections.pa}
                  onOpenChange={() => toggleSection('pa')}
                >
                  <Card className="border-2 shadow-sm">
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                              <Shield className="h-4 w-4 text-red-600" />
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold">Prior Authorization</h3>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Prior authorization requirements and criteria
                              </p>
                            </div>
                          </div>
                          {expandedSections.pa ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground transition-transform duration-200" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-4">
                  {!paRequired ? (
                    <Alert className="text-sm">
                      <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <AlertDescription className="font-normal text-xs sm:text-sm">
                        No prior authorization requirements for this drug and plan combination.
                      </AlertDescription>
                    </Alert>
                  ) : paNotesLoading ? (
                    <div className="p-4 sm:p-6 md:p-8">
                      <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-3 sm:px-4 bg-muted/30 rounded-lg border-2 border-dashed">
                        <Loader2 className="h-8 w-8 sm:h-10 sm:w-10 text-primary animate-spin mb-3 sm:mb-4" />
                        <p className="text-xs sm:text-sm font-medium text-foreground mb-1">
                          Loading Prior Authorization Notes...
                        </p>
                        <p className="text-[10px] sm:text-xs font-normal text-muted-foreground text-center max-w-md">
                          Fetching prior authorization requirements
                        </p>
                      </div>
                    </div>
                  ) : cachedPANotes?.error ? (
                    <Alert variant="destructive" className="text-sm">
                      <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <AlertDescription className="text-xs sm:text-sm">{cachedPANotes.error}</AlertDescription>
                    </Alert>
                  ) : cachedPANotes ? (
                    <StructuredNotesDisplay 
                      notes={cachedPANotes.result.StructuredNotes}
                      title="Prior Authorization Structured Notes"
                      description="Prior authorization requirements organized by indication"
                    />
                  ) : null}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>

                {/* Step Therapy Section */}
                <Collapsible
                  open={expandedSections.st}
                  onOpenChange={() => toggleSection('st')}
                >
                  <Card className="border-2 shadow-sm">
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <GitBranch className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold">Step Therapy</h3>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Step therapy requirements and alternative medications
                              </p>
                            </div>
                          </div>
                          {expandedSections.st ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground transition-transform duration-200" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-4">
                        {!stRequired ? (
                          <Alert className="text-sm">
                            <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            <AlertDescription className="font-normal text-xs sm:text-sm">
                              No step therapy requirements for this drug and plan combination.
                            </AlertDescription>
                          </Alert>
                        ) : stNotesLoading ? (
                          <div className="p-4 sm:p-6 md:p-8">
                            <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-3 sm:px-4 bg-muted/30 rounded-lg border-2 border-dashed">
                              <Loader2 className="h-8 w-8 sm:h-10 sm:w-10 text-primary animate-spin mb-3 sm:mb-4" />
                              <p className="text-xs sm:text-sm font-medium text-foreground mb-1">
                                Loading Step Therapy Notes...
                              </p>
                              <p className="text-[10px] sm:text-xs font-normal text-muted-foreground text-center max-w-md">
                                Fetching step therapy requirements
                              </p>
                            </div>
                          </div>
                        ) : cachedSTNotes?.error ? (
                          <Alert variant="destructive" className="text-sm">
                            <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            <AlertDescription className="text-xs sm:text-sm">{cachedSTNotes.error}</AlertDescription>
                          </Alert>
                        ) : cachedSTNotes ? (
                          <StructuredNotesDisplay 
                            notes={cachedSTNotes.result.StructuredNotes}
                            title="Step Therapy Structured Notes"
                            description="Step therapy requirements organized by indication"
                          />
                        ) : null}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="medical" className="mt-6">
            <MedicalNotesTab 
              indications={indications}
              productId={productId}
              formularyId={formularyId}
              cachedNotes={cachedMedicalNotes}
              onNotesUpdate={(indicationId, data) => {
                onNotesUpdate({ indicationId }, data);
              }}
            />
          </TabsContent>
        </Tabs>
    </div>
  );
};

export default NotesTab;
